import { GMTInfo } from './common';

export const WeAppInfoEndpoint = `/weApp`;

const minFuncIds = [17, 18, 25, 30, 31, 37, 40, 52, 57];

export const getMissingFuncIds: (funcIds: WeAppInfo['funcInfo'] | undefined) => WeAppInfo['funcInfo'] = funcIds => {
  if (!funcIds || !funcIds.length) {
    return minFuncIds;
  }

  const missingIds = [];

  for (let funcId of minFuncIds) {
    if (!funcIds.includes(funcId)) {
      missingIds.push(funcId);
    }
  }

  return missingIds;
};

export const funcInfoIdMap = {
  17: '帐号管理权限',
  18: '开发管理与数据分析权限',
  19: '客服消息管理权限',
  25: '开放平台帐号管理权限',
  30: '小程序基本信息设置权限',
  31: '小程序认证权限',
  36: '微信卡路里权限',
  37: '小程序附近地点权限',
  40: '小程序插件管理权限',
  41: '好物圈权限',
  45: '物流助手权限',
  48: '微信财政电子票据权限',
  49: '云开发管理权限',
  51: '即时配送权限',
  52: '小程序直播权限',
  57: '页面推送权限',
  65: '广告管理权限',
  67: '服务平台管理权限',
} as const;

export const WeAppAuditStatus = {
  // 成功
  SUCCESS: 0,

  // 拒绝
  REJECTED: 1,

  // 审核中
  IN_PROGRESS: 2,

  // 撤回
  REVERT: 3,

  // 审核中
  DELAYED: 4,

  // 已发布
  RELEASED: 5,
} as const;

/** 小程序 */
export interface WeAppInfo extends GMTInfo {
  /**小程序 id */
  id: number;

  /**企业 id */
  poiId: number;

  /**小程序 id */
  appId: string;

  funcInfo: number[];

  /** 最近一次提交的模板id */
  lastCommitTemplateId: number;

  /** 最近一次提审的模板id */
  lastAuditTemplateId: number;

  /** 最近一次发布的模板id */
  lastReleaseTemplateId: number;

  auditId: number;
  auditStatus: number;
  auditTime: string;
  auditReason: string;

  /**小程序名 */
  appName: string;

  /**小程序 secret */
  appSecret: string;

  /**商户号 */
  mchId: string;

  /**商户 secret */
  mchSecret: string;

  /**商户证书 */
  mchKeyPath: string;

  /**微信认证 */
  certified: boolean;

  /** 收银台小程序码 */
  maSettlementQrUrl: string;

  maQrUrl: string;

  appStyle: {
    primaryColor: string;
    newSkuCol: number;
    hotSkuCol: number;

    textMap: {
      [index: string]: string;
    };
  };
}

export interface WeAppTemplate {
  /**
   * 草稿id
   */
  draftId: number;
  /**
   * 模版id
   */
  templateId: number;
  /**
   * 模版版本号，开发者自定义字段
   */
  userVersion: string;
  /**
   * 模版描述 开发者自定义字段
   */
  userDesc: string;
  /**
   * 开发者上传草稿时间 / 被添加为模版的时间
   */
  createTime?: number;
}

export enum WEAPP_ENV {
  RELEASE = 'release',
  TRIAL = 'trial',
  DEVELOP = 'develop',
}

/**
 * 小程序场景值
 * @see https://developers.weixin.qq.com/miniprogram/dev/reference/scene-list.html
 * 
 * 转换规则: 
 * 
  Array.from(document.querySelectorAll('tbody tr')).reduce((acc, tr) => {
      acc[tr.children[0].innerText] = tr.children[1].firstChild.textContent.replace(/（.*?）/, '');
      return acc;
  }, {})
 */
export const WeappSceneMap = {
  '1000': '其他',
  '1001': '发现页小程序「最近使用」列表',
  '1005': '微信首页顶部搜索框的搜索结果页',
  '1006': '发现栏小程序主入口搜索框的搜索结果页',
  '1007': '单人聊天会话中的小程序消息卡片',
  '1008': '群聊会话中的小程序消息卡片',
  '1010': '收藏夹',
  '1011': '扫描二维码',
  '1012': '长按图片识别二维码',
  '1013': '扫描手机相册中选取的二维码',
  '1014': '小程序订阅消息',
  '1017': '前往小程序体验版的入口页',
  '1019': '微信钱包',
  '1020': '公众号 profile 页相关小程序列表',
  '1022': '聊天顶部置顶小程序入口',
  '1023': '安卓系统桌面图标',
  '1024': '小程序 profile 页',
  '1025': '扫描一维码',
  '1026': '发现栏小程序主入口，「附近的小程序」列表',
  '1027': '微信首页顶部搜索框搜索结果页「使用过的小程序」列表',
  '1028': '我的卡包',
  '1029': '小程序中的卡券详情页',
  '1030': '自动化测试下打开小程序',
  '1031': '长按图片识别一维码',
  '1032': '扫描手机相册中选取的一维码',
  '1034': '微信支付完成页',
  '1035': '公众号自定义菜单',
  '1036': 'App 分享消息卡片',
  '1037': '小程序打开小程序',
  '1038': '从另一个小程序返回',
  '1039': '摇电视',
  '1042': '添加好友搜索框的搜索结果页',
  '1043': '公众号模板消息',
  '1044': '带 shareTicket 的小程序消息卡片 ',
  '1045': '朋友圈广告',
  '1046': '朋友圈广告详情页',
  '1047': '扫描小程序码',
  '1048': '长按图片识别小程序码',
  '1049': '扫描手机相册中选取的小程序码',
  '1052': '卡券的适用门店列表',
  '1053': '搜一搜的结果页',
  '1054': '顶部搜索框小程序快捷入口',
  '1056': '聊天顶部音乐播放器右上角菜单',
  '1057': '钱包中的银行卡详情页',
  '1058': '公众号文章',
  '1059': '体验版小程序绑定邀请页',
  '1060': '微信支付完成页',
  '1064': '微信首页连Wi-Fi状态栏',
  '1065': 'URL scheme ',
  '1067': '公众号文章广告',
  '1068': '附近小程序列表广告',
  '1069': '移动应用通过openSDK进入微信，打开小程序',
  '1071': '钱包中的银行卡列表页',
  '1072': '二维码收款页面',
  '1073': '客服消息列表下发的小程序消息卡片',
  '1074': '公众号会话下发的小程序消息卡片',
  '1077': '摇周边',
  '1078': '微信连Wi-Fi成功提示页',
  '1079': '微信游戏中心',
  '1081': '客服消息下发的文字链',
  '1082': '公众号会话下发的文字链',
  '1084': '朋友圈广告原生页',
  '1088': '会话中查看系统消息，打开小程序',
  '1089': '微信聊天主界面下拉，「最近使用」栏',
  '1090': '长按小程序右上角菜单唤出最近使用历史',
  '1091': '公众号文章商品卡片',
  '1092': '城市服务入口',
  '1095': '小程序广告组件',
  '1096': '聊天记录，打开小程序',
  '1097': '微信支付签约原生页，打开小程序',
  '1099': '页面内嵌插件',
  '1100': '红包封面详情页打开小程序',
  '1101': '远程调试热更新',
  '1102': '公众号 profile 页服务预览',
  '1103': '发现页小程序「我的小程序」列表',
  '1104': '微信聊天主界面下拉，「我的小程序」栏',
  '1106': '聊天主界面下拉，从顶部搜索结果页，打开小程序',
  '1107': '订阅消息，打开小程序',
  '1113': '安卓手机负一屏，打开小程序',
  '1114': '安卓手机侧边栏，打开小程序',
  '1119': '【企业微信】工作台内打开小程序',
  '1120': '【企业微信】个人资料页内打开小程序',
  '1121': '【企业微信】聊天加号附件框内打开小程序',
  '1124': '扫“一物一码”打开小程序',
  '1125': '长按图片识别“一物一码”',
  '1126': '扫描手机相册中选取的“一物一码”',
  '1129': '微信爬虫访问 ',
  '1131': '浮窗',
  '1133': '硬件设备打开小程序 ',
  '1135': '小程序profile页相关小程序列表，打开小程序',
  '1144': '公众号文章 - 视频贴片',
  '1145': '发现栏 - 发现小程序',
  '1146': '地理位置信息打开出行类小程序',
  '1148': '卡包-交通卡，打开小程序',
  '1150': '扫一扫商品条码结果页打开小程序',
  '1151': '发现栏 - 我的订单',
  '1152': '订阅号视频打开小程序',
  '1153': '“识物”结果页打开小程序',
  '1154': '朋友圈内打开“单页模式”',
  '1155': '“单页模式”打开小程序',
  '1157': '服务号会话页打开小程序',
  '1158': '群工具打开小程序',
  '1160': '群待办',
  '1167': 'H5 通过开放标签打开小程序 ',
  '1168': '移动/网站应用直接运行小程序',
  '1169': '发现栏小程序主入口，各个生活服务入口',
  '1171': '微信运动记录',
  '1173': '聊天素材用小程序打开 ',
  '1175': '视频号主页商店入口',
  '1176': '视频号直播间主播打开小程序',
  '1177': '视频号直播商品',
  '1178': '在电脑打开手机上打开的小程序',
  '1179': '#话题页打开小程序',
  '1181': '网站应用打开PC小程序',
  '1183': 'PC微信 - 小程序面板 - 发现小程序 - 搜索',
  '1184': '视频号链接打开小程序',
  '1185': '群公告',
  '1186': '收藏 - 笔记',
  '1187': '浮窗或星标',
  '1189': '表情雨广告',
  '1191': '视频号活动',
  '1192': '企业微信联系人profile页',
  '1193': '视频号主页服务菜单打开小程序',
  '1194': 'URL Link ',
  '1195': '视频号主页商品tab',
  '1196': '个人状态打开小程序',
  '1197': '视频号主播从直播间返回小游戏',
  '1198': '视频号开播界面打开小游戏',
  '1200': '视频号广告打开小程序',
  '1201': '视频号广告详情页打开小程序',
  '1202': '企微客服号会话打开小程序卡片',
  '1203': '微信小程序压测工具的请求',
  '1206': '视频号小游戏直播间打开小游戏',
  '1207': '企微客服号会话打开小程序文字链',
  '1208': '聊天打开商品卡片',
  '1212': '青少年模式申请页打开小程序',
  '1215': '广告预约打开小程序',
  '1216': '视频号订单中心打开小程序',
  '1217': '蓝牙设备后台唤醒小程序',
  '1218': '微信键盘预览打开小程序',
  '1219': '视频号直播间小游戏一键上车',
  '1220': '发现页设备卡片打开小程序',
  '1223': '安卓桌面Widget打开小程序',
  '1224': '"小程序商家助手"功能打开小程序',
  '1225': '音视频通话打开小程序',
  '1226': '聊天消息在设备打开后打开小程序',
  '1228': '视频号原生广告组件打开小程序',
  '1230': '订阅号H5广告进入小程序',
  '1231': '动态消息提醒入口打开小程序',
  '1232': '搜一搜竞价广告打开小程序',
  '1233': '小程序搜索页人气游戏模块打开小游戏',
  '1238': '看一看信息流广告打开小程序',
  '1239': '视频号小游戏直播间气泡浮窗打开小游戏',
  '1242': '小程序发现页门店快送模块频道页进入小程序',
  '1243': '小程序发现页门店快送模块商品聚合页进入小程序',
  '1244': '#tag搜索结果页打开小程序',
  '1245': '小程序发现页门店快送搜索结果页进入小程序',
  '1248': '通过小程序账号迁移进入小程序',
  '1252': '搜一搜小程序搜索页「小功能」模块进入小程序',
  '1254': '发现页「动态」卡片 打开小程序',
  '1255': '发现页「我的」卡片 打开小程序',
  '1256': 'pc端小程序面板「最近使用」列表',
  '1257': 'pc端小程序面板「我的小程序」列表',
  '1258': 'pc端小程序面板「为电脑端优化」模块',
  '1259': 'pc端小程序面板「小游戏专区」模块',
  '1260': 'pc端小程序面板「推荐在电脑端使用」列表',
  '1261': '公众号返佣商品卡片',
  '1265': '小程序图片详情页打开小程序',
  '1266': '小程序图片长按半屏入口打开小程序',
  '1267': '小程序图片会话角标打开小程序',
  '1271': '微信聊天主界面下拉，「我的常用小程序」栏',
  '1272': '发现页「游戏」服务tab打开小程序',
  '1273': '发现页「常用的小程序」列表',
  '1274': '优量汇广告打开小程序',
  '1275': '发现页短剧卡片打开小程序',
  '1276': '发现页短剧卡片二级聚合页打开小程序',
  '1277': '发现页短剧卡片搜索结果页打开小程序',
  '1278': '发现页「发现小程序」列表打开小程序',
  '1279': '发现页「发现小程序」合集页打开小程序',
  '1280': '下拉任务栏小程序垂搜「建议使用」打开小程序',
  '1281': '下拉任务栏小程序垂搜「发现小程序」打开小程序',
  '1282': '听一听播放器打开小程序',
  '1285': '发现页「发现小程序」短剧合集打开小程序',
  '1286': '明文scheme打开小程序',
  '1287': '公众号短剧贴片打开小程序',
  '1292': '发现页「发现小程序」poi 详情页打开小程序',
  '1293': '发现页短剧卡片追剧页打开小程序',
  '1295': '下拉任务栏小程序垂搜「发现小程序」广告打开小程序',
  '1296': '视频号付费短剧气泡打开小程序',
  '1297': '发现-小程序-搜索「发现小程序」打开小程序',
  '1298': '下拉任务栏小程序垂搜「发现小程序」打开的合集访问小程序',
  '1299': '下拉任务栏小程序垂搜「发现小程序」poi 详情页打开小程序',
  '1300': '发现-小程序-搜索「发现小程序」打开的合集访问小程序',
  '1301': '发现-小程序-搜索「发现小程序」poi 详情页打开小程序',
  '1302': 'PC端面板「发现小程序」',
  '1303': '发现页短剧卡片视频流打开小程序',
  '1304': '手机负一屏打开小程序',
  '1305': '公众号播放结束页打开小程序',
  '1306': '公众号短剧固定选集入口打开小程序',
  '1307': '发现页附近服务境外专区打开小程序',
  '1308': 'PC端面板小游戏专区页面',
  '1311': '公众号文章打开小游戏CPS卡片',
  '1313': 'PC OpenSDK打开小程序',
  '1325': '聊天工具模式分享的表情包消息角标打开小程序',
  '1326': '聊天工具模式分享的角标打开小程序',
  '1327': '聊天工具模式分享的文本消息角标打开小程序',
  '1328': '聊天工具模式分享的文本消息超链接打开小程序',
  '1336': '小店打开小程序关联会员身份',
  '1337': '小店确认订单页跳小程序使用会员权益',
  '1340': '小程序profile-热门商品入口打开小程序',
  '1346': 'PC OpenSDK分享小程序卡片',
};

export const getWxSceneDesc = (sceneCode: string, fallback = '-') => {
  if (!sceneCode) return fallback;
  return WeappSceneMap[sceneCode] || fallback;
};

export const getSceneDesc = (sceneCode: string, fallback = '-') => {
  if (!sceneCode) return fallback;
  if (sceneCode.startsWith('wx_')) {
    return getWxSceneDesc(sceneCode.slice(3), fallback);
  }
  return fallback;
};
